import React from 'react';
import { ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { RiAddLine, RiEditLine, RiEyeLine, RiCloseLine, RiMore2Line } from 'react-icons/ri';
import { AdminAgentInfoDto } from '@repo/common-types';
import { useNavigate } from 'react-router-dom';
import routes from '@/routes/routes-enums';

interface AgentQuickActionsProps {
  agent: AdminAgentInfoDto;
  onCreateDeal: (userId: string) => void;
  onEditDeal: (dealId: string) => void;
  onDeactivateDeal: (dealId: string) => void;
  canViewProfile: boolean;
}

/**
 * Agent Quick Actions Dropdown Component
 * Provides a vertical ellipsis menu with agent-specific actions
 */
const AgentQuickActions: React.FC<AgentQuickActionsProps> = ({
  agent,
  onCreateDeal,
  onEditDeal,
  onDeactivateDeal,
  canViewProfile,
}) => {
  const navigate = useNavigate();
  const hasAgentDeal = agent.hasAgentDeal;
  const hasActiveAgentDeal = hasAgentDeal && agent.agentDeal?.isActive;

  return (
    <ButtonGroup>
      <UncontrolledDropdown>
        <DropdownToggle
          tag="button"
          type="button"
          aria-label={`More actions for agent ${agent.firstName} ${agent.lastName}`}
          className="btn btn-sm d-flex align-items-center justify-content-center"
          style={{ minWidth: '32px', padding: '4px 8px' }}
        >
          <RiMore2Line size={16} />
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-end" container="body">
          {canViewProfile ? (
            <>
              <DropdownItem
                className="d-flex justify-content-start align-items-center flex-row gap-2"
                onClick={() => navigate(routes.AGENT_PROFILE.replace(':agentId', agent.id))}
              >
                <RiEyeLine className="text-muted" size={16} />
                View Profile
              </DropdownItem>

              <DropdownItem divider />
            </>
          ) : null}

          {/* Create Agent Deal - Only show if no agent deal exists */}
          {!hasAgentDeal && (
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => onCreateDeal(agent.id)}
            >
              <RiAddLine className="text-muted" size={16} />
              Create Agent Deal
            </DropdownItem>
          )}

          {/* Edit Deal - Only show if agent deal exists */}
          {hasAgentDeal && agent.agentDeal && (
            <DropdownItem
              className="d-flex justify-content-start align-items-center flex-row gap-2"
              onClick={() => onEditDeal(agent.agentDeal?.id || '')}
            >
              <RiEditLine className="text-muted" size={16} />
              Edit Deal
            </DropdownItem>
          )}
          {/* Deactivate Deal - Only show if active agent deal exists */}
          {hasActiveAgentDeal && agent.agentDeal && (
            <>
              <DropdownItem divider />
              <DropdownItem
                className="d-flex justify-content-start align-items-center text-danger flex-row gap-2"
                onClick={() => onDeactivateDeal(agent.agentDeal?.id || '')}
              >
                <RiCloseLine className="text-danger" size={16} />
                Deactivate Deal
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    </ButtonGroup>
  );
};

export default AgentQuickActions;
